import React, {
  createContext,
  useContext,
  useState,
  useCallback,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { getSubscription } from "../api/subscription";
import { useAuth } from "./AuthContext";
import UpgradeModal from "../components/UpgradeModal";
import {
  SUBSCRIPTION_TIERS,
  getTierLimit,
  isTierLimitReached,
} from "../constants/subscriptionTiers";

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [upgradeModal, setUpgradeModal] = useState({
    open: false,
    messageKey: "GENERIC",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: getSubscription,
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
  });

  const isPremium = !!data?.isPremium;
  // Don't treat the user as FREE until the subscription has actually resolved —
  // otherwise a premium user gets falsely blocked by limit gates during the
  // initial load / auth hydration (mirrors the app's
  // `isFreeTier = loading ? false : !isPremium`). While unresolved we assume
  // premium so gates never block prematurely.
  const subResolved = isAuthenticated && !isLoading && data !== undefined;
  const isFreeTier = subResolved ? !isPremium : false;
  const tier = isFreeTier
    ? SUBSCRIPTION_TIERS.FREE
    : SUBSCRIPTION_TIERS.PREMIUM;

  const limit = useCallback((name) => getTierLimit(tier, name), [tier]);
  const isLimitReached = useCallback(
    (name, currentValue) =>
      isFreeTier ? isTierLimitReached(tier, name, currentValue) : false,
    [tier, isFreeTier]
  );

  const showUpgradeModal = useCallback((opts = {}) => {
    setUpgradeModal({ open: true, messageKey: opts.messageKey || "GENERIC" });
  }, []);
  const hideUpgradeModal = useCallback(() => {
    setUpgradeModal((s) => ({ ...s, open: false }));
  }, []);

  const value = {
    subscription: data?.subscription || null,
    promoSubscription: data?.promoSubscription || null,
    isPremium,
    isFreeTier,
    tier,
    loading: isLoading,
    limit,
    isLimitReached,
    showUpgradeModal,
    hideUpgradeModal,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      <UpgradeModal
        isOpen={upgradeModal.open}
        messageKey={upgradeModal.messageKey}
        onClose={hideUpgradeModal}
      />
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx)
    throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
};
